import { Fragment } from 'react';

import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  SelectGroup,
  SelectLabel,
} from '@/components/ui/select';
import { storageRegionOptions, assetVersionsLimitOptions } from 'src/shared/entities';
import { Badge } from '@/components/ui/badge';

type Props = {
  onStorageRegionChange: (storageRegion: string) => void;
  onAssetVersionsLimitChange: (value: string) => void;
  storageRegion: string;
  assetVersionsLimit?: number;
};

const StorageSettings = ({
  onStorageRegionChange,
  onAssetVersionsLimitChange,
  storageRegion,
  assetVersionsLimit,
}: Props) => {
  return (
    <Fragment>
      <Card>
        <CardHeader>
          <CardTitle>Storage region</CardTitle>
          <CardDescription>
            Select whether the storage of assets connected to this escrow should be limited to a specific geographical
            region as part of your regulatory requirements.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-2">
          <div className="text-sm font-semibold text-foreground">Storage region</div>
          <Select value={storageRegion ?? undefined} onValueChange={onStorageRegionChange}>
            <SelectTrigger className="w-full mb-2">
              <SelectValue placeholder="Select a region" />
            </SelectTrigger>
            <SelectContent className="group/select">
              <SelectGroup>
                <SelectLabel>Storage region</SelectLabel>
                {storageRegionOptions.map((region) => (
                  <SelectItem key={region.value} value={region.value}>
                    {region.label}
                    {region.value !== 'default' && (
                      <Badge className="absolute right-2 group-data-[state=open]/select:block hidden">Add-on</Badge>
                    )}
                  </SelectItem>
                ))}
              </SelectGroup>
            </SelectContent>
          </Select>
          <p className="text-sm text-muted-foreground">
            Selecting a specific region requires the Advanced Deposits Add-On.
          </p>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Asset version history</CardTitle>
          <CardDescription>
            Define the minimum number of most recent versions of each asset that will be held in escrow. Older versions
            will automatically be deleted.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-2">
          <div className="text-sm font-semibold text-foreground">Version history</div>
          <Select
            value={assetVersionsLimit ? String(assetVersionsLimit) : 'full'}
            onValueChange={onAssetVersionsLimitChange}
          >
            <SelectTrigger className="w-full">
              <SelectValue placeholder="Select a limit" />
            </SelectTrigger>
            <SelectContent className="group/select">
              <SelectGroup>
                <SelectLabel>Asset version limit</SelectLabel>
                {assetVersionsLimitOptions.map((option) => (
                  <SelectItem key={option.value} value={option.value}>
                    {option.label}
                    {option.value === 'full' && (
                      <Badge className="absolute right-2 group-data-[state=open]/select:block hidden">Add-on</Badge>
                    )}
                  </SelectItem>
                ))}
              </SelectGroup>
            </SelectContent>
          </Select>
          <p className="text-sm text-muted-foreground">
            Storing the full version history requires the Advanced Deposits Add-On.
          </p>
        </CardContent>
      </Card>
    </Fragment>
  );
};

export default StorageSettings;
